import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReactQuill from "react-quill-new";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, EmptyState, PolicyStatusPill, FingerprintChip } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import {
  Plus, ScrollText, Send, Save, X, Search, Fingerprint, Archive, CheckCircle2,
  ArrowLeft, RotateCcw, AlertTriangle,
} from "lucide-react";

/*
 * Resources > Policies , authoring list.
 * Reached from Sidebar > Organization > Resources > Policies card.
 * Full create / edit / draft / submit / approve / archive workflow.
 * Only reachable by HR / Company Admin / Super Admin (route gated in App.js).
 */

const CATEGORIES = ["Ethics", "People & Culture", "Privacy & Security", "Safety", "Operations", "Compensation & Benefits", "Other"];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    [{ align: [] }],
    ["clean"],
  ],
};

const TEMPLATES = [
  { key: "conduct", name: "Code of Conduct Draft", content: "<h2>Purpose</h2><p>Describe the purpose and scope of this policy.</p><h2>Applicability</h2><p>Who this applies to and when.</p><h2>Standards</h2><ul><li>Standard one.</li><li>Standard two.</li></ul><h2>Reporting Violations</h2><p>How employees can raise concerns via PropHR.</p><h2>Enforcement</h2><p>Consequences of violations.</p>" },
  { key: "harassment", name: "Anti-Harassment Draft", content: "<h2>Statement</h2><p>The Company is committed to a workplace free from harassment based on any legally protected characteristic.</p><h2>Prohibited Conduct</h2><ul><li>Verbal, physical, visual harassment.</li><li>Quid pro quo requests.</li><li>Hostile work environment.</li></ul><h2>Reporting</h2><p>Reports may be filed via PropHR anonymously.</p>" },
  { key: "privacy", name: "Data Privacy Draft (GDPR/CCPA)", content: "<h2>Purpose</h2><p>Governs the collection, processing, and retention of personal data.</p><h2>Data Subject Rights</h2><ul><li>Access, rectify, erase.</li><li>Portability.</li><li>Object to processing.</li></ul><h2>Retention</h2><p>Personal data is retained per the Records Retention Schedule.</p>" },
  { key: "remote", name: "Remote Work Draft", content: "<h2>Eligibility</h2><p>Determined by role, business need, and manager approval.</p><h2>Expectations</h2><ul><li>Safe, ergonomic workspace.</li><li>Availability during core hours.</li><li>Data security compliance.</li></ul>" },
  { key: "blank", name: "Blank policy", content: "<h2>Title</h2><p>Draft the policy content here…</p>" },
];

export default function Resources() {
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [myAcks, setMyAcks] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", category: CATEGORIES[0], summary: "", content: TEMPLATES[0].content, tags: "" });
  const [saving, setSaving] = useState(false);

  const canManage = ["hr", "company_admin", "super_admin"].includes(user?.role);
  const canRecall = ["company_admin", "super_admin"].includes(user?.role);
  const [recallFor, setRecallFor] = useState(null);
  const [recallReason, setRecallReason] = useState("");
  const [recalling, setRecalling] = useState(false);

  const doRecall = async () => {
    const reason = recallReason.trim();
    if (!reason) { toast.error("Recall reason is required"); return; }
    setRecalling(true);
    try {
      await api.post(`/policies/${recallFor.id}/recall`, { reason });
      toast.success(`Recalled ${recallFor.name}. Every user must re-sign.`);
      setRecallFor(null);
      setRecallReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recall failed");
    } finally {
      setRecalling(false);
    }
  };

  const load = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (q) params.q = q;
      const { data } = await api.get("/policies", { params });
      setPolicies(data);
    } catch { toast.error("Failed to load resources"); }
  };

  const loadAcks = async () => {
    try {
      const { data } = await api.get("/my/policy-acknowledgments");
      setMyAcks(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadAcks(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: "", category: CATEGORIES[0], summary: "", content: TEMPLATES[0].content, tags: "" });
    setCreating(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name, category: p.category, summary: p.summary || "",
      content: p.content, tags: (p.tags || []).join(", "),
    });
    setCreating(true);
  };

  const applyTemplate = (tpl) => {
    setForm({ ...form, name: form.name || tpl.name, content: tpl.content });
  };

  const saveDraft = async (submit = false) => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        summary: form.summary || null,
        content: form.content,
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      };
      let policy;
      if (editingId) {
        const { data } = await api.patch(`/policies/${editingId}`, payload);
        policy = data;
      } else {
        const { data } = await api.post("/policies", payload);
        policy = data;
      }
      if (submit) {
        const { data } = await api.post(`/policies/${policy.id}/submit`);
        policy = data;
        toast.success(policy.status === "approved" ? "Resource approved & published" : "Submitted for approval");
      } else {
        toast.success("Draft saved");
      }
      setCreating(false);
      setEditingId(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const ackedIds = useMemo(() => new Set((myAcks || []).map((a) => a.policy_id)), [myAcks]);

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="policies-authoring-page">
      <Link to="/resources" className="text-xs text-slate-500 hover:text-slate-900 font-mono flex items-center gap-1 mb-3" data-testid="back-to-resources">
        <ArrowLeft size={12}/> RESOURCES
      </Link>
      <SectionHeader
        title="Policies"
        subtitle="Author, version, and publish the policies your workspace runs on. Every publication requires approval; every version is fingerprinted for audit."
        right={canManage ? (
          <button className="btn-primary" onClick={openCreate} data-testid={TID.policyCreateBtn}>
            <Plus size={16} strokeWidth={2}/> New policy
          </button>
        ) : null}
      />

      <div className="data-cell p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2}/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search policies…" className="input pl-9" data-testid="policy-search"/>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input !w-auto" data-testid="policy-status-filter">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_approval">Pending approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="archived">Archived</option>
        </select>
        <span className="text-xs font-mono text-slate-400">{policies.length} policies</span>
      </div>

      {policies.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No policies yet"
          subtitle="Start with a template , PropHR will fingerprint the content and route it through approval."
          action={canManage ? <button className="btn-primary" onClick={openCreate} data-testid="policy-create-empty"><Plus size={16}/> New policy</button> : null}
        />
      ) : (
        <div className="space-y-3">
          {policies.map((p) => (
            <div key={p.id} className="data-cell p-5" data-testid={`policy-row-${p.id}`}>
              <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <PolicyStatusPill status={p.status}/>
                    <span className="text-[10px] font-mono text-slate-500">v{p.version} · {p.category}</span>
                    {p.effective_from && <span className="text-[10px] font-mono text-slate-500">effective {new Date(p.effective_from).toLocaleDateString()}</span>}
                    {ackedIds.has(p.id) && (
                      <span className="pill pill-approved">
                        <CheckCircle2 size={10} strokeWidth={3}/> You&apos;ve signed
                      </span>
                    )}
                  </div>
                  <Link to={`/policies/${p.id}`} className="font-display text-lg font-semibold text-slate-900 hover:underline">{p.name}</Link>
                  {p.summary && <div className="text-sm text-slate-600 mt-1">{p.summary}</div>}
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <FingerprintChip fingerprint={p.fingerprint}/>
                    {p.tags?.length > 0 && p.tags.map((t) => <span key={t} className="pill pill-closed text-[10px]">{t}</span>)}
                  </div>
                </div>
                {canManage && ["draft", "rejected"].includes(p.status) && (
                  <button className="btn-ghost text-xs" onClick={() => openEdit(p)} data-testid={`policy-edit-${p.id}`}>Edit</button>
                )}
                {canRecall && p.status === "approved" && (
                  <button
                    className="btn-ghost text-xs text-amber-800 hover:text-amber-900"
                    onClick={() => { setRecallFor(p); setRecallReason(""); }}
                    data-testid={`policy-recall-${p.id}`}
                    title="Force everyone to re-sign this policy"
                  >
                    <RotateCcw size={12} strokeWidth={2}/> Recall &amp; re-sign
                  </button>
                )}
                {p.status === "archived" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500"><Archive size={10}/> archived</span>
                )}
              </div>
              {p.status === "rejected" && p.rejection_reason && (
                <div className="text-xs text-red-700 border-l-2 border-red-500 pl-3 mt-2 italic">Rejected: {p.rejection_reason}</div>
              )}
              {p.recall_reason && (
                <div className="text-xs text-amber-800 border-l-2 border-amber-500 pl-3 mt-2 italic bg-amber-50 py-2 pr-3 rounded-r" data-testid={`recall-banner-${p.id}`}>
                  <span className="font-semibold uppercase tracking-widest text-[10px] mr-1">Recalled</span>
                  {p.recall_reason}
                  {p.recalled_at && <span className="text-amber-700 ml-1 font-mono">· {new Date(p.recalled_at).toLocaleDateString()}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {creating && (
        <PolicyEditorModal
          user={user}
          editingId={editingId}
          form={form}
          setForm={setForm}
          saving={saving}
          onClose={() => setCreating(false)}
          onSave={() => saveDraft(false)}
          onSubmit={() => saveDraft(true)}
          applyTemplate={applyTemplate}
        />
      )}

      {recallFor && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="policy-recall-modal">
          <div className="bg-white border border-amber-300 max-w-lg w-full rounded">
            <div className="flex items-center justify-between p-4 border-b border-amber-200 bg-amber-50">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2 text-amber-900">
                <AlertTriangle size={16} strokeWidth={2}/> Recall &amp; re-sign campaign
              </h3>
              <button onClick={() => { setRecallFor(null); setRecallReason(""); }} data-testid="policy-recall-close"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm text-slate-700">
                You are about to recall <span className="font-semibold text-slate-900">{recallFor.name}</span> (v{recallFor.version}).
                Every existing signature will be invalidated by fingerprint change. The policy will re-appear in the workspace signing library for every user until they re-sign.
              </div>
              <div>
                <label className="eyebrow block mb-1">Reason (required)</label>
                <textarea
                  rows={3}
                  value={recallReason}
                  onChange={(e) => setRecallReason(e.target.value)}
                  className="input"
                  placeholder="e.g. Legal correction to remote work clause following recent guidance."
                  data-testid="policy-recall-reason"
                />
              </div>
              <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 rounded">
                Prior acknowledgments remain in the audit trail. The policy version bumps to <span className="font-mono">v{(recallFor.version || 1) + 1}</span> with a new SHA-256 fingerprint.
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => { setRecallFor(null); setRecallReason(""); }}>Cancel</button>
              <button
                className="btn-primary bg-amber-700 hover:bg-amber-800 border-amber-800"
                onClick={doRecall}
                disabled={recalling}
                data-testid="policy-recall-confirm"
              >
                <RotateCcw size={14} strokeWidth={2}/>
                {recalling ? "Recalling…" : "Recall & prompt re-sign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PolicyEditorModal({ user, editingId, form, setForm, saving, onClose, onSave, onSubmit, applyTemplate }) {
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="policy-editor-modal">
      <div className="bg-white border border-slate-300 max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-semibold">{editingId ? "Edit Policy" : "New Policy"}</h3>
          <button onClick={onClose} data-testid="policy-editor-close"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1">Policy name</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input" data-testid={TID.policyName}/>
            </div>
            <div>
              <label className="eyebrow block mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input" data-testid={TID.policyCategory}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-1">Summary (optional)</label>
            <input value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} className="input" placeholder="One-sentence summary shown in listings"/>
          </div>
          <div>
            <label className="eyebrow block mb-1">Tags (comma-separated)</label>
            <input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="input" placeholder="e.g. gdpr, retention, employee data"/>
          </div>
          {!editingId && (
            <div className="border border-slate-200 bg-slate-50 p-3 rounded">
              <div className="eyebrow mb-2">Start from template</div>
              <div className="flex flex-wrap gap-2">
                {TEMPLATES.map((t) => (
                  <button key={t.key} type="button" onClick={() => applyTemplate(t)} className="btn-ghost text-xs">{t.name}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="eyebrow block mb-1">Policy content (WYSIWYG)</label>
            <ReactQuill theme="snow" value={form.content} onChange={(v) => setForm({...form, content: v})} modules={QUILL_MODULES}/>
          </div>
          <div className="border border-blue-300 bg-blue-50 p-3 rounded text-xs text-blue-900 leading-relaxed flex items-start gap-2">
            <Fingerprint size={14} className="mt-0.5 shrink-0" strokeWidth={2}/>
            <span>Every save generates a SHA-256 fingerprint. Publishing requires Company Admin approval; nothing goes live automatically. Once approved, this policy appears in the workspace Policies library for employees and managers to acknowledge.</span>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-ghost" onClick={onSave} disabled={saving} data-testid={TID.policySave}>
            <Save size={14} strokeWidth={2}/> Save as draft
          </button>
          <button className="btn-primary" onClick={onSubmit} disabled={saving} data-testid={TID.policySubmit}>
            <Send size={14} strokeWidth={2}/> {user?.role === "company_admin" ? "Save & Publish" : "Submit for approval"}
          </button>
        </div>
      </div>
    </div>
  );
}
